import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Settings, Building2, MapPin, Calculator, Shield, 
  Save, ArrowLeft, Loader2, CheckCircle, AlertTriangle 
} from 'lucide-react';

export default function FiscalConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    // Dados da empresa
    cnpj: '32772173000114',
    razao_social: 'NOVO DNA INSTITUTO DE APRENDIZAGEM',
    nome_fantasia: 'NOVO DNA',
    inscricao_municipal: '48771',
    inscricao_estadual: '',
    
    // Endereço
    logradouro: 'Estrada Benedito Cesário de Oliveira',
    numero: '156/202',
    complemento: '',
    bairro: 'Vila Iasi',
    cidade: 'Taboão da Serra',
    uf: 'SP',
    cep: '06763-480',
    codigo_municipio: '3552809',
    
    // Configuração fiscal
    codigo_servico: '08.01',
    cnae: '8513900',
    descricao_servico: 'Serviços educacionais referentes à mensalidade escolar',
    aliquota_iss: 4.0,
    
    // Regime
    optante_simples: false,
    regime_especial: 1,
    natureza_operacao: 1,
    
    // Ambiente
    ambiente: 'homologacao',
    
    // Série e numeração
    serie_nfse: '1',
    serie_rps: '1',
    codigo_ctn: '',
    codigo_nbs: '',
    cst_ibscbs: '',
    cclasstrib_ibscbs: '',
    ind_cod_ope: '',
    proximo_numero: 1,
    
    // Opções
    emissao_automatica: true,
    enviar_email_tomador: true,
    
    // Credenciais
    api_token: '',
    certificado_base64: '',
    senha_certificado: '',
    // Credenciais do portal da prefeitura
    usuario_webservice: '',
    senha_webservice: ''
  });

  const navigate = useNavigate();

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.get('/fiscal/config');
      if (response.data.configurado) {
        setConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await api.post('/fiscal/config', config);
      toast.success('Configuração fiscal salva com sucesso!');
      navigate('/fiscal');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[600px]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="fiscal-config">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/fiscal')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="w-6 h-6" />
            Configurações Fiscais
          </h1>
          <p className="text-muted-foreground">Configure os dados da empresa para emissão de NFS-e</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Tabs defaultValue="empresa" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="empresa">
              <Building2 className="w-4 h-4 mr-2" />
              Empresa
            </TabsTrigger>
            <TabsTrigger value="endereco">
              <MapPin className="w-4 h-4 mr-2" />
              Endereço
            </TabsTrigger>
            <TabsTrigger value="fiscal">
              <Calculator className="w-4 h-4 mr-2" />
              Fiscal
            </TabsTrigger>
            <TabsTrigger value="integracao">
              <Shield className="w-4 h-4 mr-2" />
              Integração
            </TabsTrigger>
          </TabsList>

          {/* Dados da Empresa */}
          <TabsContent value="empresa">
            <Card>
              <CardHeader>
                <CardTitle>Dados da Empresa</CardTitle>
                <CardDescription>Informações do prestador de serviços</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cnpj">CNPJ *</Label>
                    <Input
                      id="cnpj"
                      value={config.cnpj}
                      onChange={(e) => handleChange('cnpj', e.target.value)}
                      placeholder="00.000.000/0000-00"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="inscricao_municipal">Inscrição Municipal *</Label>
                    <Input
                      id="inscricao_municipal"
                      value={config.inscricao_municipal}
                      onChange={(e) => handleChange('inscricao_municipal', e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="razao_social">Razão Social *</Label>
                  <Input
                    id="razao_social"
                    value={config.razao_social}
                    onChange={(e) => handleChange('razao_social', e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nome_fantasia">Nome Fantasia</Label>
                  <Input
                    id="nome_fantasia"
                    value={config.nome_fantasia}
                    onChange={(e) => handleChange('nome_fantasia', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="inscricao_estadual">Inscrição Estadual</Label>
                  <Input
                    id="inscricao_estadual"
                    value={config.inscricao_estadual}
                    onChange={(e) => handleChange('inscricao_estadual', e.target.value)}
                    placeholder="Isento ou número"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Endereço */}
          <TabsContent value="endereco">
            <Card>
              <CardHeader>
                <CardTitle>Endereço do Prestador</CardTitle>
                <CardDescription>Endereço que aparecerá nas notas fiscais</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2 space-y-2">
                    <Label htmlFor="logradouro">Logradouro *</Label>
                    <Input
                      id="logradouro"
                      value={config.logradouro}
                      onChange={(e) => handleChange('logradouro', e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="numero">Número *</Label>
                    <Input
                      id="numero"
                      value={config.numero}
                      onChange={(e) => handleChange('numero', e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="complemento">Complemento</Label>
                    <Input
                      id="complemento"
                      value={config.complemento}
                      onChange={(e) => handleChange('complemento', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bairro">Bairro *</Label>
                    <Input
                      id="bairro"
                      value={config.bairro}
                      onChange={(e) => handleChange('bairro', e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cidade">Cidade *</Label>
                    <Input
                      id="cidade"
                      value={config.cidade}
                      onChange={(e) => handleChange('cidade', e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="uf">UF *</Label>
                    <Select value={config.uf} onValueChange={(v) => handleChange('uf', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SP">SP</SelectItem>
                        <SelectItem value="RJ">RJ</SelectItem>
                        <SelectItem value="MG">MG</SelectItem>
                        {/* Adicionar outros estados */}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cep">CEP *</Label>
                    <Input
                      id="cep"
                      value={config.cep}
                      onChange={(e) => handleChange('cep', e.target.value)}
                      placeholder="00000-000"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="codigo_municipio">Código IBGE do Município</Label>
                  <Input
                    id="codigo_municipio"
                    value={config.codigo_municipio}
                    onChange={(e) => handleChange('codigo_municipio', e.target.value)}
                    placeholder="3552809 (Taboão da Serra)"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Configuração Fiscal */}
          <TabsContent value="fiscal">
            <Card>
              <CardHeader>
                <CardTitle>Configuração Fiscal</CardTitle>
                <CardDescription>Parâmetros para cálculo de impostos e emissão</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="codigo_servico">Código de Serviço (LC 116) *</Label>
                    <Input
                      id="codigo_servico"
                      value={config.codigo_servico}
                      onChange={(e) => handleChange('codigo_servico', e.target.value)}
                      placeholder="08.01"
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      08.01 - Ensino regular pré-escolar, fundamental, médio e superior
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cnae">CNAE *</Label>
                    <Input
                      id="cnae"
                      value={config.cnae}
                      onChange={(e) => handleChange('cnae', e.target.value)}
                      placeholder="8513900"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="codigo_ctn">Código CTN *</Label>
                    <Input
                      id="codigo_ctn"
                      value={config.codigo_ctn || ''}
                      onChange={(e) => handleChange('codigo_ctn', e.target.value)}
                      placeholder="080101000"
                      data-testid="fiscal-config-ctn"
                    />
                    <p className="text-xs text-muted-foreground">
                      Código de Tributação Nacional — 9 dígitos numéricos (layout 4.00 da prefeitura)
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="codigo_nbs">Código NBS *</Label>
                    <Input
                      id="codigo_nbs"
                      value={config.codigo_nbs || ''}
                      onChange={(e) => handleChange('codigo_nbs', e.target.value)}
                      placeholder="120011100"
                      data-testid="fiscal-config-nbs"
                    />
                    <p className="text-xs text-muted-foreground">
                      Nomenclatura Brasileira de Serviços — 9 dígitos numéricos (Anexo VIII)
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cst_ibscbs">CST IBS/CBS *</Label>
                    <Input
                      id="cst_ibscbs"
                      value={config.cst_ibscbs || ''}
                      onChange={(e) => handleChange('cst_ibscbs', e.target.value)}
                      placeholder="200"
                      data-testid="fiscal-config-cst"
                    />
                    <p className="text-xs text-muted-foreground">
                      200 = alíquota reduzida (educação, Anexo II da LC 214/2025)
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cclasstrib_ibscbs">Classificação Tributária (cClassTrib) *</Label>
                    <Input
                      id="cclasstrib_ibscbs"
                      value={config.cclasstrib_ibscbs || ''}
                      onChange={(e) => handleChange('cclasstrib_ibscbs', e.target.value)}
                      placeholder="200028"
                      data-testid="fiscal-config-cclasstrib"
                    />
                    <p className="text-xs text-muted-foreground">
                      200028 = serviços de educação com redução de 60% (art. 129 da LC 214/2025)
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ind_cod_ope">Código da Operação (IndCodOpe) *</Label>
                    <Input
                      id="ind_cod_ope"
                      value={config.ind_cod_ope || ''}
                      onChange={(e) => handleChange('ind_cod_ope', e.target.value)}
                      data-testid="fiscal-config-indcodope"
                    />
                    <p className="text-xs text-muted-foreground">
                      Código da operação onerosa cadastrado para a empresa na prefeitura
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="descricao_servico">Descrição do Serviço *</Label>
                  <Textarea
                    id="descricao_servico"
                    value={config.descricao_servico}
                    onChange={(e) => handleChange('descricao_servico', e.target.value)}
                    rows={3}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="aliquota_iss">Alíquota ISS (%) *</Label>
                    <Input
                      id="aliquota_iss"
                      type="number"
                      step="0.01"
                      min="0"
                      max="5"
                      value={config.aliquota_iss}
                      onChange={(e) => handleChange('aliquota_iss', parseFloat(e.target.value))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="serie_nfse">Série NFS-e</Label>
                    <Input
                      id="serie_nfse"
                      value={config.serie_nfse}
                      onChange={(e) => handleChange('serie_nfse', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="serie_rps">Série do RPS</Label>
                    <Input
                      id="serie_rps"
                      value={config.serie_rps || ''}
                      onChange={(e) => handleChange('serie_rps', e.target.value)}
                      placeholder="1"
                      data-testid="fiscal-config-serie-rps"
                    />
                    <p className="text-xs text-muted-foreground">
                      Não pode ser igual à série da NFS-e
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="proximo_numero">Próximo Número</Label>
                    <Input
                      id="proximo_numero"
                      type="number"
                      value={config.proximo_numero}
                      onChange={(e) => handleChange('proximo_numero', parseInt(e.target.value))}
                    />
                  </div>
                </div>

                <div className="space-y-4 pt-4 border-t">
                  <h4 className="font-medium">Regime Tributário</h4>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="optante_simples"
                      checked={config.optante_simples}
                      onCheckedChange={(v) => handleChange('optante_simples', v)}
                    />
                    <Label htmlFor="optante_simples">Optante pelo Simples Nacional</Label>
                  </div>
                </div>

                <div className="space-y-4 pt-4 border-t">
                  <h4 className="font-medium">Automação</h4>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="emissao_automatica"
                        checked={config.emissao_automatica}
                        onCheckedChange={(v) => handleChange('emissao_automatica', v)}
                      />
                      <Label htmlFor="emissao_automatica">Emitir nota automaticamente após pagamento</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="enviar_email_tomador"
                        checked={config.enviar_email_tomador}
                        onCheckedChange={(v) => handleChange('enviar_email_tomador', v)}
                      />
                      <Label htmlFor="enviar_email_tomador">Enviar nota por e-mail ao tomador</Label>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Integração */}
          <TabsContent value="integracao">
            <Card>
              <CardHeader>
                <CardTitle>Integração com Prefeitura</CardTitle>
                <CardDescription>Credenciais e ambiente de emissão</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="ambiente">Ambiente</Label>
                  <Select value={config.ambiente} onValueChange={(v) => handleChange('ambiente', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="homologacao">Homologação (Testes)</SelectItem>
                      <SelectItem value="producao">Produção</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Use homologação para testes. Mude para produção apenas quando estiver pronto.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="api_token">Token de API da Prefeitura</Label>
                  <Input
                    id="api_token"
                    type="password"
                    value={config.api_token}
                    onChange={(e) => handleChange('api_token', e.target.value)}
                    placeholder="Token fornecido pela prefeitura"
                  />
                </div>

                {/* Certificado Digital A1 */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-4">
                  <div className="flex items-start gap-2">
                    <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-blue-800">Certificado Digital A1</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        Necessário para emissão de NFS-e em produção. Faça upload do arquivo .pfx ou .p12
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="certificado_file">Arquivo do Certificado (.pfx ou .p12)</Label>
                      <Input
                        id="certificado_file"
                        type="file"
                        accept=".pfx,.p12"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = () => {
                              const base64 = reader.result.split(',')[1];
                              handleChange('certificado_base64', base64);
                              toast.success(`Certificado "${file.name}" carregado!`);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="cursor-pointer"
                      />
                      {config.certificado_base64 && config.certificado_base64 !== '***CONFIGURADO***' && (
                        <p className="text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Certificado carregado
                        </p>
                      )}
                      {config.certificado_base64 === '***CONFIGURADO***' && (
                        <p className="text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Certificado já configurado no servidor
                        </p>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="senha_certificado">Senha do Certificado</Label>
                      <Input
                        id="senha_certificado"
                        type="password"
                        value={config.senha_certificado === '***CONFIGURADO***' ? '' : config.senha_certificado}
                        onChange={(e) => handleChange('senha_certificado', e.target.value)}
                        placeholder={config.senha_certificado === '***CONFIGURADO***' ? '••••••••' : 'Digite a senha do certificado'}
                      />
                      {config.senha_certificado === '***CONFIGURADO***' && (
                        <p className="text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Senha já configurada
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {config.ambiente === 'producao' && !config.certificado_base64 && (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-yellow-800">Atenção</h4>
                        <p className="text-sm text-yellow-700 mt-1">
                          Você selecionou ambiente de produção, mas o certificado A1 não está configurado.
                          A emissão de notas fiscais não funcionará sem o certificado.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Credenciais do Portal da Prefeitura */}
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg space-y-4">
                  <div className="flex items-start gap-2">
                    <Shield className="w-5 h-5 text-purple-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-purple-800">Credenciais do Portal da Prefeitura</h4>
                      <p className="text-sm text-purple-700 mt-1">
                        Login e senha do webservice da Prefeitura de Taboão da Serra para emissão de NFS-e
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="usuario_webservice">Usuário do Webservice</Label>
                      <Input
                        id="usuario_webservice"
                        type="text"
                        value={config.usuario_webservice}
                        onChange={(e) => handleChange('usuario_webservice', e.target.value)}
                        placeholder="Usuário de acesso ao portal"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="senha_webservice">Senha do Webservice</Label>
                      <Input
                        id="senha_webservice"
                        type="password"
                        value={config.senha_webservice === '***CONFIGURADO***' ? '' : config.senha_webservice}
                        onChange={(e) => handleChange('senha_webservice', e.target.value)}
                        placeholder={config.senha_webservice === '***CONFIGURADO***' ? '••••••••' : 'Senha de acesso ao portal'}
                      />
                      {config.senha_webservice === '***CONFIGURADO***' && (
                        <p className="text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Senha já configurada
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Botões de ação */}
        <div className="flex justify-end gap-4 pt-6">
          <Button type="button" variant="outline" onClick={() => navigate('/fiscal')}>
            Cancelar
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Salvar Configurações
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
